import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';

import Button from '../../components/ui/Button';
import InteractiveMap from './components/InteractiveMap';
import ClusteringVisualization from './components/ClusteringVisualization';
import FilterControls from './components/FilterControls';
import NarrativeSummaryPanel from './components/NarrativeSummaryPanel';

const NarrativeExplorer = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedNarrative, setSelectedNarrative] = useState(null);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [timelinePosition, setTimelinePosition] = useState(75);
  const [viralityThreshold, setViralityThreshold] = useState(50);
  const [harmThreshold, setHarmThreshold] = useState(60);
  const [coordinationThreshold, setCoordinationThreshold] = useState(0.7);
  const [activeView, setActiveView] = useState('split');
  const [filters, setFilters] = useState({
    dateRange: {
      from: '2024-09-01',
      to: '2024-09-21'
    },
    countries: [],
    themes: [],
    sources: [],
    thresholds: {
      confidence: 70,
      virality: 50
    }
  });

  // Real-time data simulation
  useEffect(() => {
    const interval = setInterval(() => {
      // Simulate real-time updates
      setTimelinePosition(prev => {
        const newPos = prev + Math.random() * 2 - 1;
        return Math.max(0, Math.min(100, newPos));
      });
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleCountrySelect = (countryCode) => {
    setSelectedCountries(prev => 
      prev?.includes(countryCode) 
        ? prev?.filter(c => c !== countryCode)
        : [...prev, countryCode]
    );
  };

  const handleNarrativeSelect = (narrativeId) => {
    setSelectedNarrative(narrativeId);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleFiltersReset = () => {
    setFilters({
      dateRange: {
        from: '2024-09-01',
        to: '2024-09-21'
      },
      countries: [],
      themes: [],
      sources: [],
      thresholds: {
        confidence: 70,
        virality: 50
      }
    });
  };

  const handleSavePreset = (name, filterData) => {
    // Save preset logic
    console.log('Saving preset:', name, filterData);
  };

  const handleExportReport = (narrativeId) => {
    // Export report logic
    console.log('Exporting report for:', narrativeId);
  };

  const handleEscalate = (narrativeId) => {
    // Navigate to crisis response center
    navigate('/crisis-response-center', { 
      state: { escalatedNarrative: narrativeId } 
    });
  };

  const handleViewEvidence = (claimId) => {
    // Navigate to claim detail analysis
    navigate('/claim-detail-analysis', { 
      state: { claimId } 
    });
  };

  const handleClusterAnalyze = (threshold) => {
    setCoordinationThreshold(threshold);
  };

  const viewModes = [
    { id: 'split', label: 'Split View', icon: 'Columns' },
    { id: 'map', label: 'Map Only', icon: 'Map' },
    { id: 'cluster', label: 'Cluster Only', icon: 'GitBranch' },
    { id: 'summary', label: 'Summary Only', icon: 'FileText' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`
        transition-all duration-300 ease-in-out pt-16
        ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}
      `}>
        <div className="p-6">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-2">Narrative Explorer</h1>
              <p className="text-muted-foreground">
                Visualize evolving disinformation campaigns and cross-country coordination patterns
              </p>
            </div>

            <div className="flex items-center space-x-3">
              {/* View Mode Toggle */}
              <div className="flex items-center space-x-1 bg-muted/50 rounded-lg p-1">
                {viewModes?.map((mode) => (
                  <Button
                    key={mode?.id}
                    variant={activeView === mode?.id ? "default" : "ghost"}
                    size="sm"
                    iconName={mode?.icon}
                    onClick={() => setActiveView(mode?.id)}
                    className="px-3"
                  />
                ))}
              </div>

              {/* Quick Actions */}
              <Button
                variant="outline"
                iconName="RefreshCw"
                iconPosition="left"
                onClick={() => {
                  // Refresh data
                }}
              >
                Refresh
              </Button>

              <Button
                variant="outline"
                iconName="Settings"
                iconPosition="left"
                onClick={() => {
                  // Open settings
                }}
              >
                Settings
              </Button>
            </div>
          </div>

          {/* Real-time Status Bar */}
          <div className="flex items-center justify-between p-4 bg-card border border-border rounded-lg shadow-soft mb-6">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-foreground">Live Monitoring Active</span>
              </div>
              
              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                <span>24 Active Narratives</span>
                <span>•</span>
                <span>156 Claims Tracked</span>
                <span>•</span>
                <span>12 Countries Monitored</span>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <span className="text-sm text-muted-foreground">
                Last Update: {new Date()?.toLocaleTimeString()}
              </span>
              
              <Button
                variant="outline"
                size="sm"
                iconName="Bell"
                iconPosition="left"
              >
                Alerts (3)
              </Button>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-12 gap-6">
            {/* Filters Sidebar */}
            <div className="col-span-12 lg:col-span-3">
              <FilterControls
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onReset={handleFiltersReset}
                onSavePreset={handleSavePreset}
              />
            </div>

            {/* Visualization Area */}
            <div className="col-span-12 lg:col-span-9">
              <div className="space-y-6">
                {/* Interactive Map */}
                {(activeView === 'split' || activeView === 'map') && (
                  <InteractiveMap
                    selectedCountries={selectedCountries}
                    timelinePosition={timelinePosition}
                    viralityThreshold={viralityThreshold}
                    harmThreshold={harmThreshold}
                    onCountrySelect={handleCountrySelect}
                    onTimelineChange={setTimelinePosition}
                  />
                )}

                {/* Clustering Visualization */}
                {(activeView === 'split' || activeView === 'cluster') && (
                  <ClusteringVisualization
                    selectedNarratives={selectedNarrative ? [selectedNarrative] : []}
                    coordinationThreshold={coordinationThreshold}
                    onNarrativeSelect={handleNarrativeSelect}
                    onClusterAnalyze={handleClusterAnalyze}
                  />
                )}

                {/* Narrative Summary Panel */}
                {(activeView === 'split' || activeView === 'summary') && (
                  <NarrativeSummaryPanel
                    selectedNarrative={selectedNarrative}
                    onExportReport={handleExportReport}
                    onEscalate={handleEscalate}
                    onViewEvidence={handleViewEvidence}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Mobile-Optimized Bottom Panel */}
          <div className="lg:hidden mt-6">
            <div className="bg-card rounded-lg border border-border shadow-soft p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground">Quick Actions</h3>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="MoreHorizontal"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  iconName="Search"
                  iconPosition="left"
                  onClick={() => navigate('/public-claim-search')}
                  fullWidth
                >
                  Search Claims
                </Button>
                
                <Button
                  variant="outline"
                  iconName="AlertTriangle"
                  iconPosition="left"
                  onClick={() => navigate('/crisis-response-center')}
                  fullWidth
                >
                  Crisis Center
                </Button>
                
                <Button
                  variant="outline"
                  iconName="Users"
                  iconPosition="left"
                  onClick={() => navigate('/expert-console-dashboard')}
                  fullWidth
                >
                  Expert Console
                </Button>
                
                <Button
                  variant="outline"
                  iconName="Database"
                  iconPosition="left"
                  onClick={() => navigate('/research-data-portal')}
                  fullWidth
                >
                  Research Portal
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default NarrativeExplorer;